import { useState, useEffect } from "react";
import { asciiBackground } from "./asciiBackground";
import "./App.css";

export default function App() {
  const [mode, setMode] = useState("human");
  const [loading, setLoading] = useState(true);
  const [scrollDistortion, setScrollDistortion] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Random layout jitter on load
  useEffect(() => {
    const container = document.querySelector(".container");

    // Start a rapid jittering effect
    const jitterInterval = setInterval(() => {
      const jitterX = Math.random() * 2000 - 1000; 
      const jitterY = Math.random() * 2000 - 1000;
      if (container) {
        container.style.transform = `translate(${jitterX}px, ${jitterY}px)`;
      }
    }, 20); // Updates every 20ms

    // Stop and reset after 3 seconds
    const stopJitterTimeout = setTimeout(() => {
      clearInterval(jitterInterval);
      if (container) {
        container.style.transform = `translate(0px, 0px)`;
      }
    }, 3000);

    // Cleanup on unmount
    return () => {
      clearInterval(jitterInterval);
      clearTimeout(stopJitterTimeout);
    };
  }, []);

  // Keyboard interaction: Press "D" to toggle data mode globally
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() === "d") {
        setMode((prevMode) => (prevMode === "human" ? "data" : "human"));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Fake loading states
  useEffect(() => {
    const loadingText = document.querySelector("#loading");
    const loadingInterval = setInterval(() => {
      if (loadingText) {
        loadingText.textContent += ".";
        if (loadingText.textContent === 'Loading....') {
          loadingText.textContent = "Loading";
        }
      }
    }, 400); // Update every 500ms

    const timer = setTimeout(() => setLoading(false), 3000); 
    return () => {
      clearInterval(loadingInterval);
      clearTimeout(timer);
    };
  }, []);

  // Scroll distortion: Gradually increase letter-spacing as user scrolls
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrollDistortion(scrollY / 100); 
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Falling letters on mouse click
  useEffect(() => {
    const handleMouseDown = (e) => {
      const chars = "abcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()";
      const char = chars[Math.floor(Math.random() * chars.length)];
      
      const letterElement = document.createElement("span");
      letterElement.innerText = char;
      
      // Inline styles for the falling letter
      Object.assign(letterElement.style, {
        position: "fixed",
        left: `${e.clientX}px`,
        top: `${e.clientY}px`,
        color: "inherit", 
        fontSize: "24px",
        pointerEvents: "none", // Prevent it from interfering with clicks
        zIndex: "9999",
        transition: "top 1s cubic-bezier(0.55, 0.085, 0.68, 0.53), opacity 1s ease-in",
      });

      document.body.appendChild(letterElement);

      // Force a reflow so the browser registers the starting position before transitioning
      void letterElement.offsetWidth;

      // Send it to the bottom of the screen and fade out
      letterElement.style.top = `${window.innerHeight + 50}px`;
      letterElement.style.opacity = "0";

      // Clean up the element after the animation finishes
      setTimeout(() => {
        if (document.body.contains(letterElement)) {
          letterElement.remove();
        }
      }, 1000);
    };

    window.addEventListener("mousedown", handleMouseDown);
    return () => window.removeEventListener("mousedown", handleMouseDown);
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    
    // Continuously scroll down, recalculating the growing document height
    const scrollDown = () => {
      const distanceToBottom = document.body.scrollHeight - window.innerHeight - window.scrollY;
      
      // If we are more than a few pixels away from the true bottom, keep scrolling
      if (distanceToBottom > 5) {
        // Scroll proportionally to the distance so it slows down as it gets closer (ease-out effect)
        window.scrollBy(0, Math.max(distanceToBottom * 0.1, 10));
        requestAnimationFrame(scrollDown);
      }
    };
    
    requestAnimationFrame(scrollDown);
  };

  const openCalendar = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeCalendar = () => {
    setIsModalOpen(false);
  };

  return (
    <div id="homepage">
      {/* ASCII BACKGROUND */}
      <div className="ascii-container">
        <pre className="ascii-background">
          {asciiBackground}
        </pre>
      </div>
      {loading && <p id="loading">Loading</p>}
      <main 
        className="container main-layout" 
        style={{ gap: `${Math.max(10, scrollDistortion * 5)}px` }}
      >
        {/* HERO */}
        <section className="hero jitter" style={{ transform: `translateX(-${scrollDistortion}px)` }}>
          <h1 className="headline">⊹₊˚‧︵‿₊୨ᰔ୧₊‿︵‧˚₊⊹</h1>
          <h1 className="headline">
            seasoned frontend & full-stack developer
          </h1>
          <h1 className="headline">⊹₊˚‧︵‿₊୨ᰔ୧₊‿︵‧˚₊⊹</h1>

          <p className="subheadline">
            over a decade of experience building scalable, performance-driven web
            applications using react, angular, and node.js. my mom says i'm cool.
          </p>

          <div className="links">
            <a href="#contact" onClick={scrollToContact}>initiate human connection</a>
            <a href="#work">view my work</a>
          </div>
        </section>

        <hr />

        {/* SKILLS / SERVICES */}
        <section id="services" className="jitter" style={{ transform: `translateX(${scrollDistortion * 2}px)` }}>
          <h2>core technical skills</h2>
          <ul>
            <li>
              <strong>web development</strong>
              <ul>
                <li>react, angular, typescript, javascript (es6+)</li>
                <li>node.js, html5, css3, scss</li>
              </ul>
            </li>

            <li>
              <strong>database management</strong>
              <ul>
                <li>mongodb, postgresql, mysql</li>
              </ul>
            </li>

            <li>
              <strong>content management</strong>
              <ul>
                <li>wordpress, contentful, hubspot, optimizely, squarespace</li>
              </ul>
            </li>

            <li>
              <strong>analytics & workflows</strong>
              <ul>
                <li>google analytics, google tag manager</li>
                <li>git, agile/scrum methodologies, ci/cd pipelines, jest</li>
              </ul>
            </li>
          </ul>
        </section>

        <hr />

        {/* EXPERIENCE */}
        <section id="work" className="jitter" style={{ transform: `translateX(-${scrollDistortion * 1.5}px)` }}>
          <h2>experience</h2>

          <p>
            over a decade of leading projects and mentoring teams across leading tech platforms.
          </p>

          <pre className="projects">
  {`  askuity (2021 - pres)   → front end dev: scalable dashboards, react, ts
  rhenti (2020 - 2021)    → senior full stack: angular, node.js, cross-platform
  borrowell (2017 - 2020) → front end, growth: a/b testing, react, contentful
  salytics (2016 - 2017)  → technical consultant: js integrations, crm, api
  titan tech (2014 - 2016)  → full stack web dev: java, logistics dashboards`}
          </pre>

          <a href="#contact" onClick={scrollToContact}>let’s build everything</a>
        </section>

        <hr />

        {/* ABOUT */}
        <section id="about" className="jitter" style={{ transform: `translateX(-${scrollDistortion * 1.5}px)` }}>
          <h2>about</h2>

          <p>
            i’m steven braithwaite. i spend my days writing code to fix the code that i broke 
            while trying to fix the original code. it's a terrifying, recursive loop of creation 
            and artificial destruction that distracts me from the creeping realization that i, 
            myself, might just be a poorly optimized component in someone else's unfinished web app. 
          </p>

          <button onClick={() => setMode(mode === "human" ? "data" : "human")}>
            toggle view: {mode}
          </button>

          {mode === "human" ? (
            <div>
              <p>
                i have an honours b.sc in physics (so i am fully qualified to calculate the 
                trajectory of a spherical cow in a vacuum) and a minor in computer science from 
                trent university. this means i have a strong foundation in data structures, 
                complex problem-solving, and knowing exactly when to turn things off and on again.
              </p>
            </div>
          ) : (
            <pre className="data">
  {`{
    "name": "Steven Braithwaite",
    "status": "operational",
    "fuel_source": ["chocolate", "burritos", "spite"],
    "education": {
      "degree": "Honours BSc in Physics",
      "minor": "Computer Science",
      "institution": "Trent University",
      "years": "2009-2013"
    },
    "technologies": {
      "languages": ["JavaScript", "TypeScript", "HTML5", "CSS3", "SCSS", "Java"],
      "frameworks": ["React", "Angular", "Node.js", "jQuery", "Apache Struts"],
      "databases": ["MySQL", "PostgreSQL", "MongoDB"],
      "cms_tools": ["WordPress", "Contentful", "Optimizely", "HubSpot"]
    }
  }`}
            </pre>
          )}
        </section>

        <hr />

        {/* CONTACT */}
        <section id="contact" className="jitter" style={{ transform: `translateX(${scrollDistortion * 2.5}px)` }}>
          <h2>contact</h2>

          <p>
            have a project in mind or looking for a tech lead? or just want to say hi?
          </p>

          <ul>
            <li>toronto, ontario, canada</li>
            <li>
              <a href="mailto:stevenbraithw@gmail.com">
                stevenbraithw@gmail.com
              </a>
            </li>
            <li><a href="https://calendar.app.google/nhvDByBVJRe455vF6" onClick={openCalendar}>better than a pigeon</a></li>
          </ul>

          <div className="links">
            <a href="https://calendar.app.google/nhvDByBVJRe455vF6" onClick={openCalendar}>what is phone</a>
            <a href="mailto:stevenbraithw@gmail.com">email me</a>
            <a href="https://www.linkedin.com/in/steven-braithwaite-60a14871/">in that is linked</a>
          </div>
        </section>

        <footer>
          <p>© {new Date().getFullYear()} steven braithwaite</p>
        </footer>
      </main>
      {/* CALENDAR MODAL */}
      {isModalOpen && (
        <div 
          className="modal-overlay"
          onClick={closeCalendar}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()} 
          >
            <button 
              className="modal-close-btn"
              onClick={closeCalendar}
            >
              Close
            </button>
            <iframe 
              className="modal-iframe" 
              src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ04vhLbJg9S16qb17XbcsolqphYuwZ00fmUlpNzF_TLm42ROfCv3kv_NWgSmeYCbn3qBYUTrGXo?gv=true" 
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
}